// components/dropdown/dropdown.jsx
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FiChevronDown } from "react-icons/fi";

import "./dropdown.scss";

/**
 * Lightweight hover dropdown with:
 * - Portal menu (escapes overflow:hidden parents)
 * - Smooth fade/slide via CSS
 * - Grace period after mouse leaves the hover zone
 * - Options can be ANY ReactNode (SVGs, icons + text, etc.)
 *
 * Props:
 *  - options: Array<{
 *      value: string|number,
 *      content?: React.ReactNode,
 *      label?: React.ReactNode
 *    }>
 *  - value?: controlled value
 *  - defaultValue?: uncontrolled initial value
 *  - onChange?: (value) => void
 *  - placeholder?: string|React.ReactNode
 *  - hoverPadding?: number
 *  - usePortal?: boolean
 *  - className?: string
 *  - hasCaret?: boolean
 */
export default function HoverDropdown({
  options = [],
  value,
  defaultValue,
  onChange,
  placeholder = "Select…",
  hoverPadding = 12,
  usePortal = true,
  className,
  hasCaret = false,
  buttonTitle,
}) {
  const isControlled = value !== undefined;

  const [internal, setInternal] = useState(
    defaultValue ?? options[0]?.value ?? null
  );

  const selectedValue = isControlled ? value : internal;

  const [open, setOpen] = useState(false);
  const closeTimer = useRef(null);
  const triggerRef = useRef(null);

  const selected = options.find((option) => option.value === selectedValue);

  const clearTimer = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  const scheduleClose = () => {
    clearTimer();
    closeTimer.current = setTimeout(() => setOpen(false), 1000);
  };

  const handleEnter = () => {
    clearTimer();
    setOpen(true);
  };

  const handleLeave = () => {
    scheduleClose();
  };

  const select = (value) => {
    if (!isControlled) setInternal(value);

    onChange?.(value);
    setOpen(false);
  };

  const [menuPos, setMenuPos] = useState({
    top: 0,
    left: 0,
    width: 0,
  });

  const updatePosition = () => {
    const element = triggerRef.current;
    if (!element) return;

    const rect = element.getBoundingClientRect();

    setMenuPos({
      top: rect.bottom + 6,
      left: rect.left,
      width: rect.width,
    });
  };

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const reflow = () => updatePosition();

    window.addEventListener("scroll", reflow, true);
    window.addEventListener("resize", reflow);

    return () => {
      window.removeEventListener("scroll", reflow, true);
      window.removeEventListener("resize", reflow);
    };
  }, [open]);

  const renderMenu = (portal = false) => (
    <div
      className={`hd__menu ${portal ? "hd__menu--portal" : ""} ${
        open ? "is-open" : ""
      }`}
      style={
        portal
          ? {
              top: menuPos.top,
              left: menuPos.left,
              width: menuPos.width,
              position: "fixed",
            }
          : undefined
      }
      role="listbox"
      aria-hidden={!open}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      {options.map((option) => (
        <div
          key={String(option.value)}
          role="option"
          className={`hd__item ${
            option.value === selectedValue ? "is-active" : ""
          }`}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => select(option.value)}
        >
          {option.content ?? option.label ?? String(option.value)}
        </div>
      ))}
    </div>
  );

  return (
    <div
      className={`hd ${className || ""}`}
      style={{
        padding: hoverPadding,
        display: "inline-block",
      }}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      <button
        ref={triggerRef}
        type="button"
        className="hd__button"
        title={buttonTitle}
        onClick={() => setOpen((current) => !current)}
      >
        <span className="hd__selected">
          {selected
            ? selected.content ?? selected.label ?? String(selected.value)
            : placeholder}
        </span>

        {hasCaret && (
          <span className="hd__caret" aria-hidden>
            <FiChevronDown size={16} />
          </span>
        )}
      </button>

      {!usePortal && renderMenu()}
      {usePortal && createPortal(renderMenu(true), document.body)}
    </div>
  );
}
